import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auth } from '../firebase';
import { groupReviewsService } from '../services/groupReviews';
import './AutoScan.css';

const BACKEND_ROOT = ((process.env.REACT_APP_API_BASE_URL || 'https://api.cyberpatrolling.in/api')
  .replace(/\/api\/?$/, '')
  .replace(/\/$/, ''));

const API_KEY = process.env.REACT_APP_API_KEY || 'gph-firebase-2026-x8k9mP4nQ7tJ2wL5vY6zA3bN1rH8sD9fG0cU';
const AUTO_SCAN_STATE_SAVE_DELAY_MS = 800;
const MAX_PERSISTED_RESULTS = 2000;
const MAX_PERSISTED_RUNS = 100;
const ANY_STATE_VALUE = '__any_state__';

const initialState = {
  queue: [],
  watchlist: [],
  completedRuns: [],
  results: [],
  phase: 'idle', // 'idle' | 'phase1' | 'phase2' | 'waiting' | 'done' | 'error'
  phase1Progress: { done: 0, total: 0 },
  phase2Progress: { batchDone: 0, batchTotal: 0, groupsDone: 0, groupsTotal: 0 },
  discoveredLinksCount: 0,
  batchPauseUntil: null,
  pipelineOperationId: null,
  scheduleMode: 'weekly',
  nextRunAt: null,
  targetState: 'Goa',
  targetCity: '',
  queryLocationMode: 'state_only',
  maxGroups: 130,
  searchLanguages: ['English'],
  lastCycleFinishedAt: null,
};


const locationModes = [
  { value: 'state_only', label: 'Use state only' },
  { value: 'city_only', label: 'Use city only' },
  { value: 'state_city', label: 'Use state + city' },
  { value: 'any', label: 'Any state' },
];

const INDIAN_STATE_OPTIONS = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
];

function normalizeKeywordList(value) {
  return value
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function groupKey(group) {
  return String(
    group?.username ||
    group?.link ||
    group?.url ||
    group?.group_link ||
    group?.groupLink ||
    group?.invite_link ||
    group?.id ||
    group?.chat_id ||
    group?.title ||
    group?.groupName ||
    group?.group_name ||
    ''
  ).toLowerCase();
}

function buildSearchPhrase(keyword, config = {}) {
  const base = String(keyword || '').trim();
  const state = String(config.targetState || '').trim();
  const city = String(config.targetCity || '').trim();
  const mode = config.queryLocationMode || 'state_only';
  const parts = [base];

  if (mode === 'state_only' && state) {
    parts.push(state);
  } else if (mode === 'city_only' && city) {
    parts.push(city);
  } else if (mode === 'state_city' || mode === 'both') {
    if (state) parts.push(state);
    if (city) parts.push(city);
  }

  return parts.filter(Boolean).join(' ');
}

function resultSearchPhrase(group, fallbackConfig = {}) {
  const direct = group?.autoScanSearchPhrase || group?.searchPhrase || group?.keyword || group?.autoScanKeyword || group?.query;
  if (direct) return String(direct);
  if (group?.link) return String(group.link).replace(/https?:\/\/t\.me\//i, '@');
  return buildSearchPhrase(fallbackConfig.targetState || '', fallbackConfig);
}

function locationDisplay(value, fallback = '') {
  if (value && typeof value === 'object') {
    const label = String(value.label || '').trim();
    if (label) return label;
    const city = String(value.city || '').trim();
    const state = String(value.state || '').trim();
    const combined = [city, state].filter(Boolean).join(', ');
    if (combined) return combined;
  }
  if (value && typeof value === 'string' && value.trim() && value !== '__any_state__') {
    return value.trim();
  }
  return fallback && fallback !== '__any_state__' ? fallback : '';
}

function makeAutoScanId(keyword, seed = Date.now()) {
  const slug = String(keyword || 'keyword')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 36) || 'keyword';
  return `autoscan_${seed}_${slug}`;
}

function normalizeQueueItem(item, fallbackConfig = {}, index = 0) {
  const source = typeof item === 'string' ? { keyword: item } : (item || {});
  const keyword = String(source.keyword || '').trim();
  if (!keyword) return null;

  const mode = source.queryLocationMode || fallbackConfig.queryLocationMode || initialState.queryLocationMode;
  const needsCity = mode === 'city_only' || mode === 'state_city' || mode === 'both';
  const queuedAt = source.queuedAt || new Date(Date.now() + index).toISOString();
  const targetState = mode === 'any' ? '' : String(source.targetState ?? fallbackConfig.targetState ?? '').trim();
  const targetCity = needsCity ? String(source.targetCity ?? fallbackConfig.targetCity ?? '').trim() : '';
  const config = {
    targetState,
    targetCity,
    queryLocationMode: mode,
  };

  return {
    id: source.id || source.autoScanId || makeAutoScanId(keyword, Date.parse(queuedAt) || Date.now() + index),
    keyword,
    searchPhrase: source.searchPhrase || buildSearchPhrase(keyword, config),
    queuedAt,
    scheduleMode: source.scheduleMode || fallbackConfig.scheduleMode || initialState.scheduleMode,
    maxGroups: Number(source.maxGroups ?? fallbackConfig.maxGroups) || initialState.maxGroups,
    searchLanguages: Array.isArray(source.searchLanguages) && source.searchLanguages.length
      ? source.searchLanguages
      : (Array.isArray(fallbackConfig.searchLanguages) && fallbackConfig.searchLanguages.length
        ? fallbackConfig.searchLanguages
        : initialState.searchLanguages),
    ...config,
  };
}

function queueIdentity(item) {
  const normalized = normalizeQueueItem(item);
  if (!normalized) return '';
  return [
    normalized.keyword,
    normalized.queryLocationMode,
    normalized.targetState,
    normalized.targetCity,
  ].join('|').toLowerCase();
}

function queueItemId(item) {
  return typeof item === 'string' ? queueIdentity(item) : (item?.id || queueIdentity(item));
}

function decisionOf(group) {
  const text = String(
    group?.manualDecision ||
    group?.reviewStatus ||
    group?.reviewDecision ||
    group?.status ||
    group?.llmDecision ||
    group?.llm_decision ||
    group?.llmLabel ||
    group?.llm_label ||
    group?.aiDecision ||
    group?.ai_decision ||
    group?.aiLabel ||
    group?.ai_label ||
    group?.aiVerdict ||
    group?.ai_verdict ||
    group?.decision ||
    group?.verdict ||
    ''
  ).toLowerCase();

  if (text.includes('fraud') || text.includes('fake')) return 'fraud';
  if (text.includes('susp')) return 'suspicious';
  if (text.includes('genuine')) return 'genuine';
  if (text.includes('uncertain')) return 'uncertain';
  return 'unmarked';
}

function summarize(groups) {
  const list = Array.isArray(groups) ? groups : [];
  return list.reduce(
    (acc, group) => {
      acc.total += 1;
      const dec = decisionOf(group);
      if (acc[dec] !== undefined) acc[dec] += 1;
      return acc;
    },
    { total: 0, fraud: 0, suspicious: 0, genuine: 0, uncertain: 0, unmarked: 0 }
  );
}

function displayName(group) {
  return group?.title || group?.groupName || group?.group_name || group?.name || group?.username || group?.link || 'Telegram group';
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function numberValue(...values) {
  const value = firstDefined(...values);
  if (value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}



function percentDisplay(value) {
  if (value === undefined || value === null || value === '') return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  if (parsed >= 0 && parsed <= 1) return `${Math.round(parsed * 100)}%`;
  return `${Math.round(parsed)}%`;
}

function aiDecisionDisplay(value) {
  const decision = decisionOf({ llmDecision: value });
  if (decision === 'fraud') return 'Fraud';
  if (decision === 'suspicious') return 'Suspicious';
  if (decision === 'genuine') return 'Genuine';
  if (decision === 'uncertain') return 'Uncertain';
  return value ? String(value) : 'Unmarked';
}



function evidenceDisplay(value) {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value !== 'object') return String(value);

  const parts = [
    value.term,
    value.match,
    value.keyword,
    value.label,
    value.field,
    value.source,
    value.state,
    value.city,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean);

  if (parts.length) return Array.from(new Set(parts)).join(' - ');

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function linkForGroup(group) {
  const direct = firstDefined(group?.groupLink, group?.group_link, group?.link, group?.url, group?.invite_link);
  if (direct) return direct;
  const username = String(group?.username || '').replace(/^@/, '').trim();
  return username ? `https://t.me/${username}` : '';
}

function parseList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // fall through
    }
    return value.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function engineList(group) {
  const engines = parseList(firstDefined(
    group?.discoveredByEngines,
    group?.discovered_by_engines,
    group?.source_engines,
    group?.sourceEngines
  ));
  const single = firstDefined(group?.sEngine, group?.source, group?.engine);
  return Array.from(new Set([...engines, single].filter(Boolean)));
}

function latestActivity(group) {
  return firstDefined(
    group?.lastActiveDate,
    group?.last_active_date,
    group?.lastMessageDate,
    group?.last_message_date,
    group?.lastActive,
    group?.last_active
  );
}

function normalizeAutoScanState(savedState) {
  if (!savedState || typeof savedState !== 'object') return initialState;

  const restored = { ...initialState, ...savedState };
  restored.queue = Array.isArray(restored.queue)
    ? restored.queue.map((item, index) => normalizeQueueItem(item, restored, index)).filter(Boolean)
    : [];
  restored.watchlist = Array.isArray(restored.watchlist)
    ? restored.watchlist.map((item, index) => normalizeQueueItem(item, restored, index)).filter(Boolean)
    : [];
  restored.completedRuns = Array.isArray(restored.completedRuns) ? restored.completedRuns : [];
  restored.results = Array.isArray(restored.results) ? restored.results : [];
  restored.searchLanguages = Array.isArray(restored.searchLanguages) && restored.searchLanguages.length
    ? restored.searchLanguages
    : initialState.searchLanguages;
  restored.scheduleMode = ['weekly', 'monthly'].includes(restored.scheduleMode)
    ? restored.scheduleMode
    : initialState.scheduleMode;

  // If cycle is done or idle, filter out queued items that are already in watchlist
  if (['done', 'idle'].includes(restored.phase) && restored.watchlist.length > 0) {
    const watchlistKeys = new Set(restored.watchlist.map(queueIdentity));
    restored.queue = restored.queue.filter((qItem) => !watchlistKeys.has(queueIdentity(qItem)));
  }

  return restored;
}

function compactAutoScanState(state) {
  const normalized = normalizeAutoScanState(state);

  return {
    ...normalized,
    queue: normalized.queue.slice(0, 500),
    watchlist: normalized.watchlist.slice(0, 500),
    completedRuns: normalized.completedRuns.slice(0, MAX_PERSISTED_RUNS),
    results: normalized.results.slice(0, MAX_PERSISTED_RESULTS),
  };
}

/* ── Rich-card helper functions (mirrors TelegramSearch) ── */
const hitsRiskLevel = (score) => {
  const n = Number(score) || 0;
  if (n >= 25) return 'critical';
  if (n >= 15) return 'high';
  if (n >= 5) return 'medium';
  return 'low';
};
const riskColour = (r) => ({ critical: '#dc2626', high: '#d97706', medium: '#0369a1', low: '#16a34a' }[r] || '#64748b');
const langFlag = (l) => ({ EN: '🇬🇧', PT: '🇵🇹', RU: '🇷🇺' }[(l || '').toUpperCase()] || '🌐');

const matchedKeywordHitCount = (value) => {
  if (Array.isArray(value)) return value.length;
  if (!value || typeof value !== 'object') return 0;
  return Object.values(value).reduce((sum, v) => {
    if (Array.isArray(v)) return sum + v.length;
    const n = Number(v); return sum + (Number.isFinite(n) ? n : 1);
  }, 0);
};

const parseExternalLinksRich = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'number') return new Array(Math.max(0, value)).fill('link-item');
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try { const p = JSON.parse(trimmed.replace(/'/g, '"')); if (Array.isArray(p)) return p; } catch { }
  if (trimmed.includes(',')) return trimmed.split(',').map(l => l.trim()).filter(Boolean);
  return /^https?:\/\//i.test(trimmed) ? [trimmed] : [];
};

const normalizeLlmDecisionRich = (value) => {
  const raw = (value || '').toString().trim().toLowerCase();
  if (!raw) return 'unknown';
  if (raw.includes('likely_fraud') || raw === 'fraud' || raw === 'fraudulent') return 'fraud';
  if (raw.includes('likely_legit') || raw === 'genuine' || raw === 'legit') return 'genuine';
  if (raw.includes('uncertain')) return 'uncertain';
  return 'unknown';
};

const signalDisplayRich = (status) => {
  if (status === 'fraud') return 'Fraud';
  if (status === 'uncertain') return 'Uncertain';
  return 'Genuine';
};

const signalStyleRich = (status) => ({
  background: status === 'fraud' ? '#fff7ed' : status === 'uncertain' ? '#fffbeb' : '#f0fdf4',
  color: status === 'fraud' ? '#c2410c' : status === 'uncertain' ? '#b45309' : '#166534',
});

const aiBadgeStyleRich = (decision) => ({
  background: decision === 'fraud' ? '#fef2f2' : decision === 'uncertain' ? '#fffbeb' : '#f0fdf4',
  color: decision === 'fraud' ? '#dc2626' : decision === 'uncertain' ? '#b45309' : '#166534',
});

const getSignalDecisionsRich = (group) => {
  const matchedKeywords = group?.matchedKeywords ?? group?.matched_keywords ?? {};
  const externalLinks = group?.externalLinks ?? group?.external_links ?? [];
  const keywordHits = matchedKeywordHitCount(matchedKeywords);
  const externalCount = parseExternalLinksRich(externalLinks).length;
  const keywordFlagged = keywordHits > 5;
  const externalFlagged = externalCount > 20;
  const bkl = group?.matchedKeywordLabel ?? group?.matched_keyword_label;
  const bel = group?.externalLinkLabel ?? group?.external_link_label;
  return {
    keywordStatus: bkl || (keywordFlagged ? 'fraud' : 'genuine'),
    externalStatus: bel || (externalFlagged ? 'fraud' : 'genuine'),
    keywordHits, externalCount, keywordFlagged, externalFlagged,
  };
};

const getDecisionLabelsRich = (group) => {
  const signals = getSignalDecisionsRich(group);
  const aiLabel = normalizeLlmDecisionRich(
    group?.llmDecision ?? group?.llm_decision ?? group?.llmLabel ?? group?.llm_label ?? ''
  );
  const final = aiLabel !== 'unknown' ? aiLabel : 'uncertain';
  const matchedKeywordLabel = group?.matchedKeywordLabel ?? group?.matched_keyword_label ?? signals.keywordStatus;
  const externalLinkLabel = group?.externalLinkLabel ?? group?.external_link_label ?? signals.externalStatus;
  const ruleFraud = matchedKeywordLabel === 'fraud' && externalLinkLabel === 'fraud';
  const overallLabel = final === 'fraud' || ruleFraud ? 'fraud'
    : final === 'genuine' && matchedKeywordLabel === 'genuine' && externalLinkLabel === 'genuine' ? 'genuine'
      : 'uncertain';
  return { aiLabel: final, matchedKeywordLabel, externalLinkLabel, overallLabel, signals };
};

const keywordRelatedValueRich = (group) => {
  const raw = group?.keywordRelated ?? group?.keyword_related;
  if (typeof raw === 'boolean') return raw;
  if (raw == null || raw === '') return null;
  const text = String(raw).trim().toLowerCase();
  if (['true', 'yes', 'y', '1'].includes(text)) return true;
  if (['false', 'no', 'n', '0'].includes(text)) return false;
  return null;
};

function SignalReasonStripRich({ signals }) {
  return (
    <div style={{ marginTop: 10, padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#475569', lineHeight: 1.45 }}>
      <strong style={{ color: '#0f172a' }}>Signal check:</strong>{' '}
      External links are <strong style={{ color: signals.externalFlagged ? '#c2410c' : '#166534' }}>{signals.externalCount}</strong>{' '}
      {signals.externalFlagged ? 'which is above the 20-link fraud threshold' : 'which stays within the safe 20-link limit'}.
      {' '}Matched keyword hits are <strong style={{ color: signals.keywordFlagged ? '#c2410c' : '#166534' }}>{signals.keywordHits}</strong>{' '}
      {signals.keywordFlagged ? 'which is above the 5-hit fraud threshold' : 'which stays within the safe 5-hit limit'}.
    </div>
  );
}

function AutoScanGroupCard({ group, index, fallbackConfig, onMarkReview }) {
  const [showPhrases, setShowPhrases] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [showLocationMatches, setShowLocationMatches] = useState(false);
  const [showPhoneMatches, setShowPhoneMatches] = useState(false);

  const link = linkForGroup(group);
  const title = displayName(group);
  const members = numberValue(group.memberCount, group.member_count, group.members);
  const totalHits = numberValue(group.totalHits, group.total_hits);
  const phraseHits = numberValue(group.phraseHits, group.phrase_hits, group.evidenceHits, group.evidence_hits);
  const activeAt = latestActivity(group);
  const engines = engineList(group);
  const isPublic = firstDefined(group.isPublic, group.is_public);
  const hasPhoto = firstDefined(group.hasPhoto, group.has_photo);
  const description = firstDefined(group.description, group.group_description, group.snippet);
  const phraseMatches = parseList(firstDefined(group.phraseMatches, group.phrase_matches));
  const locationMatches = parseList(firstDefined(group.locationMatches, group.location_matches));
  const extractedPhones = parseList(firstDefined(group.extractedPhoneNumbers, group.extracted_phone_numbers));
  const phoneMatches = parseList(firstDefined(group.phoneNumberMatches, group.phone_number_matches));
  const searchPhraseStr = resultSearchPhrase(group, fallbackConfig);
  const groupLoc = firstDefined(group.locationTarget, group.targetState, group.target_state, group.location_target, group.state);
  const stateFromPhrase = INDIAN_STATE_OPTIONS.find((st) =>
    st.toLowerCase() !== '__any_state__' && searchPhraseStr.toLowerCase().includes(st.toLowerCase())
  );
  const locationLabel = locationDisplay(groupLoc, stateFromPhrase || (fallbackConfig?.targetState !== '__any_state__' ? fallbackConfig?.targetState : ''));

  // Rich signal/label helpers
  const decisionLabels = getDecisionLabelsRich(group);
  const signals = decisionLabels.signals;
  const aiReason = firstDefined(group.llmReason, group.llm_reason, group.aiNote, group.ai_note);
  const confidence = percentDisplay(firstDefined(group.llmConfidence, group.llm_confidence));
  const kwRelated = keywordRelatedValueRich(group);
  const kwRelatedReason = firstDefined(group.keywordRelatedReason, group.keyword_related_reason, '');

  // External links
  const extLinksRaw = firstDefined(group.externalLinkItems, group.external_link_items, group.externalLinks, group.external_links);
  const extLinksArr = parseExternalLinksRich(extLinksRaw);
  const extTotal = numberValue(group.externalLinksTotal, group.external_links_total) || extLinksArr.length;
  const linkSamples = extLinksArr.filter(u => /^https?:\/\//i.test(String(u)));

  // Risk level / colour
  const score = group.score || (totalHits + phraseHits + extTotal);
  const rLevel = group.risk_level || hitsRiskLevel(score);

  // Manual review state
  const userReviewedDecision = firstDefined(group.manualDecision, group.reviewDecision, group.reviewStatus);
  const isUserReviewed = Boolean(userReviewedDecision);

  return (
    <div className="list-item" style={{ borderLeft: `4px solid ${riskColour(rLevel)}`, marginBottom: 16, borderRadius: 10, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ padding: '14px 16px' }}>

        {/* ── Title Row ── */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          <span style={{ color: '#94a3b8', fontWeight: 600, marginRight: 4 }}>{index + 1}.</span>
          <strong style={{ fontSize: 15, color: '#0f172a' }}>{title}</strong>

          {isPublic !== null && isPublic !== undefined && (
            <span className="tag" style={{ background: isPublic ? '#eff6ff' : '#f1f5f9', color: isPublic ? '#1d4ed8' : '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>
              {isPublic ? 'Public' : 'Private'}
            </span>
          )}

          <span className="tag" style={{ ...signalStyleRich(decisionLabels.overallLabel), fontSize: '0.75rem', fontWeight: 800 }}>
            Overall:{signalDisplayRich(decisionLabels.overallLabel)}
          </span>

          {locationLabel && (
            <span className="tag" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: 700 }}>
              📍 {locationLabel}
            </span>
          )}

          {activeAt && (
            <span className="tag" style={{ marginLeft: 'auto', background: '#e2e8f0', color: '#334155', fontSize: '0.75rem', fontWeight: 500 }}>
              Last Active: {new Date(activeAt).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* ── Link ── */}
        {link && (
          <div style={{ marginBottom: 10 }}>
            <a href={link} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 13, wordBreak: 'break-all' }}>{link}</a>
          </div>
        )}

        {/* ── Meta Grid ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', fontSize: 13, color: '#334155', marginBottom: 10 }}>
          <div><strong>Members:</strong> {members ? members.toLocaleString() : 'N/A'}</div>
          {hasPhoto !== null && hasPhoto !== undefined && <div><strong>Photos:</strong> {hasPhoto === true ? 'Yes' : 'No'}</div>}
          <div><strong>Total Hits:</strong> {totalHits}</div>
          <div><strong>Evidence Hits:</strong> {phraseHits}</div>
          <div><strong>External Links:</strong> {extTotal}</div>
          <div><strong>Matched Keywords:</strong> {signals.keywordHits}</div>
          <div><strong>Phone Numbers:</strong> <span style={{ color: extractedPhones.length > 0 ? '#15803d' : '#334155', fontWeight: extractedPhones.length > 0 ? 700 : 400 }}>{extractedPhones.length}</span></div>
        </div>

        {/* ── Signal Chips ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
          <span className="tag" title={`LLM confidence: ${confidence || 'N/A'}`} style={{ ...aiBadgeStyleRich(decisionLabels.aiLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            AI:{decisionLabels.aiLabel === 'fraud' ? 'Fraud' : decisionLabels.aiLabel === 'genuine' ? 'Genuine' : 'Uncertain'}{confidence ? ` ${confidence}` : ''}
          </span>
          {kwRelated !== null && (
            <span className="tag" title={kwRelatedReason || (kwRelated ? 'Matches the search topic' : 'Does not match')}
              style={{ background: kwRelated ? '#ecfdf5' : '#fef2f2', color: kwRelated ? '#047857' : '#b91c1c', fontSize: '0.75rem', fontWeight: 700 }}>
              Keyword Related:{kwRelated ? 'Yes' : 'No'}
            </span>
          )}
          <span className="tag" title={`Matched keyword hits: ${signals.keywordHits}/5`}
            style={{ ...signalStyleRich(decisionLabels.matchedKeywordLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            Matched Keywords:{signalDisplayRich(decisionLabels.matchedKeywordLabel)}
          </span>
          <span className="tag" title={`External links: ${signals.externalCount}/20`}
            style={{ ...signalStyleRich(decisionLabels.externalLinkLabel), fontSize: '0.75rem', fontWeight: 700 }}>
            External Links:{signalDisplayRich(decisionLabels.externalLinkLabel)}
          </span>
          {/* {extractedPhones.length > 0 && (
            <span className="tag" title={`Extracted Phone Numbers: ${extractedPhones.length}`}
              style={{ background: '#dcfce7', color: '#15803d', border: '1px solid #86efac', fontSize: '0.75rem', fontWeight: 700 }}>
              📞 Phone Numbers: {extractedPhones.length}
            </span>
          )} */}
          <span className="tag" style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.75rem', fontWeight: 600 }}>
            Keyword: {resultSearchPhrase(group, fallbackConfig)}
          </span>
        </div>

        {/* ── Signal Check Strip ── */}
        <SignalReasonStripRich signals={signals} />

        {/* ── S-Engine ── */}
        {engines.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
            <strong>S-engine:</strong>{' '}
            {engines.map((eng) => (
              <span key={eng} className="tag" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '0.7rem', fontWeight: 600, marginRight: 4, padding: '2px 6px' }}>
                {eng.charAt(0).toUpperCase() + eng.slice(1)}
              </span>
            ))}
          </div>
        )}

        {/* ── Description ── */}
        {description && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, maxHeight: 120, overflowY: 'auto' }}>
            <strong style={{ color: '#475569', fontSize: 11, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Description</strong>
            <div style={{ fontSize: '12.5px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{description}</div>
          </div>
        )}

        {/* ── AI Intelligence Note ── */}
        {aiReason && (
          <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: '13.5px', lineHeight: 1.5, color: '#166534' }}>
            <strong style={{ color: '#166534', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>AI Intelligence Note</strong>
            {aiReason}
          </div>
        )}

        {/* ── Extracted Phone Numbers ── */}
        {extractedPhones.length > 0 && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
            <strong style={{ color: '#166534', fontSize: 11, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
              📱 Contact Numbers Found ({extractedPhones.length})
            </strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {extractedPhones.map((p) => (
                <a key={p} href={`tel:${p}`} style={{ background: '#ffffff', color: '#15803d', border: '1px solid #86efac', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  {p}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* ── Phone Number Matches (Evidence) ── */}
        {phoneMatches.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={() => setShowPhoneMatches(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#15803d', fontWeight: 700, padding: '4px 0' }}>
              {showPhoneMatches ? '▲ Hide Phone Matches' : `▼ View ${phoneMatches.length} Phone Number Message Contexts`}
            </button>
            {showPhoneMatches && (
              <div style={{ marginTop: 10 }}>
                {phoneMatches.map((m, i) => (
                  <div key={i} style={{ background: '#f0fdf4', padding: 12, marginBottom: 10, borderRadius: 8, border: '1px solid #bbf7d0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, marginBottom: 6 }}>
                      <div>
                        <span className="tag" style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700 }}>📍 {m.found_in || 'message_scan'}</span>
                      </div>
                      {m.direct_link && (
                        <a href={m.direct_link} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, fontSize: 11 }}>
                          Open Message ↗
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#166534', fontWeight: 700 }}>
                      Phones: {(m.phone_numbers || []).join(', ')}
                    </div>
                    {m.message_text && (
                      <div style={{ fontSize: 12, color: '#334155', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        {m.message_text}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Phrase Matches (Evidence) ── */}
        {phraseMatches.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={() => setShowPhrases(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#2563eb', fontWeight: 600, padding: '4px 0' }}>
              {showPhrases ? '▲ Hide Evidence Context' : `▼ View ${phraseMatches.length} Keyword Context Hits`}
            </button>
            {showPhrases && (
              <div style={{ marginTop: 10 }}>
                {phraseMatches.slice(0, 10).map((m, i) => (
                  <div key={i} style={{ background: '#f8fafc', padding: 12, marginBottom: 10, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, marginBottom: 6 }}>
                      <div>
                        <span className="tag" style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>{langFlag(m.language)} {(m.language || '').toUpperCase()}</span>
                        {m.found_in && <span className="tag" style={{ background: '#fef3c7', color: '#92400e', marginLeft: 6, fontWeight: 700 }}>📍 {m.found_in}</span>}
                        {(m.matched_phrase || m.matched_word) && <span style={{ marginLeft: 8, color: '#475569' }}>Hit: <strong style={{ color: '#0f172a' }}>"{m.matched_phrase || m.matched_word}"</strong></span>}
                      </div>
                      {m.date && m.date !== 'N/A' && <span style={{ color: '#94a3b8' }}>{new Date(m.date).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>}
                    </div>
                    {m.message_text && (
                      <div style={{ fontSize: 13, lineHeight: 1.5, color: '#1e293b', background: '#fff', padding: '8px 10px', borderRadius: 6, borderLeft: '3px solid #cbd5e1', marginBottom: 8 }}>
                        {m.message_text.slice(0, 300)}{m.message_text.length > 300 ? '…' : ''}
                      </div>
                    )}
                    {m.direct_link && (
                      <a href={m.direct_link} target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, color: '#2563eb', textDecoration: 'none', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, background: '#eff6ff', padding: '4px 8px', borderRadius: 4 }}>
                        🔗 VIEW IN TELEGRAM
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Location Matches ── */}
        {locationMatches.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={() => setShowLocationMatches(o => !o)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#92400e', fontWeight: 600, padding: '4px 0' }}>
              {showLocationMatches ? `▲ Hide location matches` : `▼ View ${locationMatches.length} location match${locationMatches.length > 1 ? 'es' : ''}`}
            </button>
            {showLocationMatches && (
              <div style={{ marginTop: 8 }}>
                {locationMatches.slice(0, 8).map((m, i) => (
                  <div key={i} style={{ background: '#fffbeb', padding: 10, marginBottom: 6, borderRadius: 6, border: '1px solid #fde68a', fontSize: 12.5 }}>
                    <span style={{ color: '#334155' }}>{evidenceDisplay(m)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── External Links ── */}
        {linkSamples.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <strong style={{ fontSize: 13 }}>External Links ({linkSamples.length} of {extTotal}):</strong>
            <div style={{ marginTop: 4 }}>
              {linkSamples.slice(0, showLinks ? linkSamples.length : 3).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noreferrer"
                  style={{ display: 'block', fontSize: 12, color: '#475569', textDecoration: 'none', marginBottom: 2 }}>
                  {u.length > 70 ? u.slice(0, 70) + '…' : u}
                </a>
              ))}
              {linkSamples.length > 3 && (
                <span onClick={() => setShowLinks(s => !s)}
                  style={{ fontSize: 12, cursor: 'pointer', color: '#2563eb' }}>
                  {showLinks ? 'Read less' : `Read more (+${linkSamples.length - 3})`}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Review Actions ── */}
        <div style={{ marginTop: 14, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          {isUserReviewed ? (
            <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
              ✅ Reviewed: {aiDecisionDisplay(userReviewedDecision)}
              {group.reviewReason && <span style={{ fontWeight: 400, marginLeft: 8 }}>— {group.reviewReason}</span>}
            </div>
          ) : (
            <div className="autoscan-review-actions">
              <button type="button" className="autoscan-review-btn fraud" onClick={() => onMarkReview(group, 'fake')}>Mark Fraudulent</button>
              <button type="button" className="autoscan-review-btn suspicious" onClick={() => onMarkReview(group, 'suspicious')}>Mark Suspicious</button>
              <button type="button" className="autoscan-review-btn genuine" onClick={() => onMarkReview(group, 'genuine')}>Mark Genuine</button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}




export default function AutoScan() {
  const [autoState, setAutoState] = useState(initialState);
  const [isStateLoaded, setIsStateLoaded] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterVerdict, setFilterVerdict] = useState('all');
  const [showClearConfirmModal, setShowClearConfirmModal] = useState(false);
  const PAGE_SIZE = 50;

  const resultsList = useMemo(() => (Array.isArray(autoState?.results) ? autoState.results : []), [autoState?.results]);
  const queueList = useMemo(() => (Array.isArray(autoState?.queue) ? autoState.queue : []), [autoState?.queue]);
  const watchlistList = useMemo(() => (Array.isArray(autoState?.watchlist) ? autoState.watchlist : []), [autoState?.watchlist]);

  const filteredResults = useMemo(() => {
    if (filterVerdict === 'all') return resultsList;
    return resultsList.filter((group) => {
      const dec = decisionOf(group);
      if (filterVerdict === 'fraud') return dec === 'fraud' || dec === 'fraudulent' || dec === 'fake';
      if (filterVerdict === 'suspicious') return dec === 'suspicious' || dec === 'susp';
      if (filterVerdict === 'genuine') return dec === 'genuine';
      if (filterVerdict === 'uncertain') return dec === 'uncertain';
      if (filterVerdict === 'unmarked') return !dec || dec === 'unmarked' || dec === 'none';
      return true;
    });
  }, [resultsList, filterVerdict]);

  const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE) || 1;
  const displayedResults = useMemo(() => {
    const startIdx = (currentPage - 1) * PAGE_SIZE;
    return filteredResults.slice(startIdx, startIdx + PAGE_SIZE);
  }, [filteredResults, currentPage]);

  // Dedicated Global Telegram session state
  const [tgSession, setTgSession] = useState({ connected: false, authorized: false, loading: true });
  const [showSessionSetup, setShowSessionSetup] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [tgBusy, setTgBusy] = useState(false);

  const stateRef = useRef(autoState);
  const persistTimerRef = useRef(null);

  useEffect(() => {
    stateRef.current = autoState;
  }, [autoState]);

  const requestBackend = useCallback(async (path, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      ...(options.headers || {}),
    };

    if (auth.currentUser) {
      headers.Authorization = `Bearer ${await auth.currentUser.getIdToken()}`;
    }

    const response = await fetch(`${BACKEND_ROOT}${path}`, {
      ...options,
      headers,
    });

    const text = await response.text();
    let payload = {};
    try {
      payload = text ? JSON.parse(text) : {};
    } catch {
      payload = { detail: text };
    }

    if (!response.ok) {
      throw new Error(payload.detail || payload.message || `Request failed with ${response.status}`);
    }

    return payload;
  }, []);

  // Fetch Global Telegram Session Status
  const checkTgSessionStatus = useCallback(async () => {
    try {
      const res = await requestBackend('/api/global-autoscan/session/status');
      setTgSession({
        connected: Boolean(res.connected),
        authorized: Boolean(res.authorized),
        loading: false,
      });
    } catch (e) {
      setTgSession({ connected: false, authorized: false, loading: false });
    }
  }, [requestBackend]);

  useEffect(() => {
    checkTgSessionStatus();
  }, [checkTgSessionStatus]);

  // Load and poll global Firestore state
  useEffect(() => {
    let cancelled = false;

    const loadGlobalState = async () => {
      try {
        const data = await requestBackend('/api/global-autoscan/state');
        if (cancelled) return;
        if (data?.state) {
          setAutoState(normalizeAutoScanState(data.state));
        }
      } catch (loadError) {
        if (!cancelled) {
          console.warn('Could not load global AutoScan state:', loadError.message);
        }
      } finally {
        if (!cancelled) setIsStateLoaded(true);
      }
    };

    loadGlobalState();
    const interval = setInterval(loadGlobalState, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [requestBackend]);

  // Save global state on user modifications
  const saveStateToBackend = useCallback((newState) => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = setTimeout(async () => {
      try {
        await requestBackend('/api/global-autoscan/state', {
          method: 'PUT',
          body: JSON.stringify({ state: compactAutoScanState(newState) }),
        });
      } catch (saveError) {
        console.error('Could not save global AutoScan state:', saveError.message);
      }
    }, AUTO_SCAN_STATE_SAVE_DELAY_MS);
  }, [requestBackend]);

  const updateStateAndPersist = useCallback((updater) => {
    setAutoState((prev) => {
      const partial = typeof updater === 'function' ? updater(prev) : updater;
      const next = normalizeAutoScanState({ ...prev, ...partial });
      saveStateToBackend(next);
      return next;
    });
  }, [saveStateToBackend]);

  // Telegram Session Auth Handlers
  const handleSendTgCode = async () => {
    const cleanPhone = phoneInput.trim();
    if (!cleanPhone || !cleanPhone.startsWith('+')) {
      setError('Enter Telegram phone with country code (e.g. +919876543210)');
      return;
    }
    setError('');
    setMessage('');
    setTgBusy(true);
    try {
      await requestBackend('/api/global-autoscan/session/send-code', {
        method: 'POST',
        body: JSON.stringify({ phone: cleanPhone }),
      });
      setCodeSent(true);
      setMessage('Verification code sent! Check Telegram on your phone.');
    } catch (e) {
      setError(`Failed to send code: ${e.message}`);
    } finally {
      setTgBusy(false);
    }
  };

  const handleConfirmTgCode = async () => {
    const cleanPhone = phoneInput.trim();
    const cleanCode = codeInput.trim();
    if (!cleanPhone || !cleanCode) {
      setError('Phone and OTP code are required.');
      return;
    }
    setError('');
    setMessage('');
    setTgBusy(true);
    try {
      await requestBackend('/api/global-autoscan/session/confirm', {
        method: 'POST',
        body: JSON.stringify({
          phone: cleanPhone,
          code: cleanCode,
          password: passwordInput || undefined,
        }),
      });
      setMessage('Global Telegram Session Authorized Successfully!');
      setShowSessionSetup(false);
      setCodeSent(false);
      setCodeInput('');
      setPasswordInput('');
      checkTgSessionStatus();
    } catch (e) {
      setError(`Authorization failed: ${e.message}`);
    } finally {
      setTgBusy(false);
    }
  };

  const handleLogoutTgSession = async () => {
    if (!window.confirm('Log out the shared global Telegram session? You will need to sign in with Telegram again to run AutoScan.')) {
      return;
    }
    setTgBusy(true);
    try {
      await requestBackend('/api/global-autoscan/session/logout', { method: 'POST' });
      setMessage('Global Telegram session logged out.');
      checkTgSessionStatus();
    } catch (e) {
      setError(`Logout failed: ${e.message}`);
    } finally {
      setTgBusy(false);
    }
  };

  // Pipeline Restart / Stop
  const handleRestartAllKeywords = async () => {
    if (!tgSession.authorized) {
      setError('Authorized Global Telegram Session required before starting global scan.');
      setShowSessionSetup(true);
      return;
    }

    const allKeywordItems = [...autoState.watchlist, ...autoState.queue];
    const uniqueItemsMap = new Map();
    allKeywordItems.forEach((raw, idx) => {
      const normalized = normalizeQueueItem(raw, autoState, idx);
      if (normalized) {
        const id = queueIdentity(normalized);
        if (!uniqueItemsMap.has(id)) {
          uniqueItemsMap.set(id, normalized);
        }
      }
    });

    const reQueueItems = Array.from(uniqueItemsMap.values());
    if (!reQueueItems.length) {
      setError('No monitored keywords found. Please add keywords first.');
      return;
    }

    const searchPhrasesForBackend = reQueueItems.map((item) => item.searchPhrase || item.keyword);

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }

    setError('');
    setMessage(`Restarting global AutoScan for all ${reQueueItems.length} monitored keyword(s)...`);

    const existingLinks = (autoState.results || []).map((r) => r.link || r.group_link || r.username).filter(Boolean);

    try {
      const res = await requestBackend('/api/global-autoscan/start', {
        method: 'POST',
        body: JSON.stringify({
          keywords: searchPhrasesForBackend,
          targetState: autoState.targetState,
          targetCity: autoState.targetCity,
          queryLocationMode: autoState.queryLocationMode,
          existingLinks,
          batchSize: 100,
          pauseMinutes: 10,
        }),
      });

      updateStateAndPersist((prev) => ({
        ...prev,
        phase: 'phase1',
        queue: reQueueItems,
        phase1Progress: { done: 0, total: reQueueItems.length },
        pipelineOperationId: res.operation_id,
      }));
      setMessage(`Global AutoScan restarted for all ${reQueueItems.length} keywords! Operation ID: ${res.operation_id}`);
    } catch (e) {
      setError(`Failed to restart pipeline: ${e.message}`);
    }
  };

  const handleStopGlobalPipeline = async () => {
    try {
      await requestBackend('/api/global-autoscan/stop', { method: 'POST' });
      setMessage('Stop requested. Pipeline will pause/stop at the next batch checkpoint.');
    } catch (e) {
      setError(`Failed to stop pipeline: ${e.message}`);
    }
  };

  const addKeywords = async () => {
    const keywords = normalizeKeywordList(keywordInput);
    if (!keywords.length) return;

    const baseConfig = {
      scheduleMode: autoState.scheduleMode,
      targetState: autoState.targetState,
      targetCity: autoState.targetCity,
      queryLocationMode: autoState.queryLocationMode,
      maxGroups: autoState.maxGroups,
      searchLanguages: autoState.searchLanguages,
    };

    const watchlistKeys = new Set(autoState.watchlist.map(queueIdentity));
    const nextWatchlistItems = [];

    keywords.forEach((keyword, index) => {
      const queuedItem = normalizeQueueItem({
        keyword,
        queuedAt: new Date(Date.now() + index).toISOString(),
        ...baseConfig,
      }, baseConfig, index);
      if (!queuedItem) return;

      const key = queueIdentity(queuedItem);

      if (!watchlistKeys.has(key)) {
        nextWatchlistItems.push(queuedItem);
        watchlistKeys.add(key);
      }
    });

    if (!nextWatchlistItems.length) {
      setKeywordInput('');
      setError('');
      setMessage('Those keywords are already in the monitored list.');
      return;
    }

    const addedKeywordList = nextWatchlistItems.map((item) => item.searchPhrase || item.keyword);
    const updatedWatchlist = [...autoState.watchlist, ...nextWatchlistItems];

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }

    setKeywordInput('');
    setError('');

    if (tgSession.authorized) {
      setMessage(`${addedKeywordList.length} keyword(s) added! Starting Phase 1 link discovery...`);
      try {
        await requestBackend('/api/global-autoscan/phase1/add', {
          method: 'POST',
          body: JSON.stringify({
            keywords: addedKeywordList,
            targetState: autoState.targetState,
            targetCity: autoState.targetCity,
            queryLocationMode: autoState.queryLocationMode,
          }),
        });

        updateStateAndPersist((prev) => ({
          ...prev,
          watchlist: updatedWatchlist,
          queue: [...prev.queue, ...nextWatchlistItems],
          phase: 'phase1',
        }));
        setMessage(`${addedKeywordList.length} keyword(s) added & Phase 1 link discovery started!`);
      } catch (e) {
        console.warn('Phase 1 auto-start warning:', e.message);
        updateStateAndPersist((prev) => ({
          ...prev,
          watchlist: updatedWatchlist,
          queue: [...prev.queue, ...nextWatchlistItems],
        }));
      }
    } else {
      updateStateAndPersist((prev) => ({
        ...prev,
        watchlist: updatedWatchlist,
        queue: [...prev.queue, ...nextWatchlistItems],
      }));
      setMessage(`${addedKeywordList.length} keyword(s) added to queue. Please authorize Telegram session.`);
    }
  };

  const removeQueuedKeyword = (id) => {
    updateStateAndPersist((prev) => ({
      ...prev,
      queue: prev.queue.filter((item) => queueItemId(item) !== id),
      watchlist: prev.watchlist.filter((item) => queueItemId(item) !== id),
    }));
  };



  const clearResults = () => {
    setShowClearConfirmModal(true);
  };

  const handleConfirmClearResults = () => {
    updateStateAndPersist((prev) => ({ ...prev, results: [] }));
    setCurrentPage(1);
    setShowClearConfirmModal(false);
    setMessage('Accumulated global AutoScan results cleared.');
  };

  const handleMarkReview = useCallback(async (group, status) => {
    const reviewLabel = status === 'fake' ? 'fraudulent' : status === 'suspicious' ? 'suspicious' : 'genuine';
    const reason = window.prompt(`Please enter reason why this group is ${reviewLabel}:`, '');

    if (reason === null) return;
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError('Reason is required before submitting review.');
      return;
    }

    const proceed = window.confirm(`Confirm marking this group as ${reviewLabel}?\n\nReason: ${trimmedReason}`);
    if (!proceed) return;

    const groupLink = linkForGroup(group);
    const groupName = displayName(group);

    if (!groupLink && !groupName) {
      setError('Could not identify this Telegram group for review.');
      return;
    }

    const reviewerId = auth.currentUser?.email || 'admin';
    const reviewedAt = new Date().toISOString();
    const reportId = group.scanReportId || group.scan_report_id || group.globalAutoScanOperationId;
    const reviewData = {
      groupLink,
      groupName,
      status,
      reason: trimmedReason,
      timestamp: reviewedAt,
      officerId: reviewerId,
      source: 'autoscan_review',
      keywordQuery: resultSearchPhrase(group, stateRef.current),
      sEngine: engineList(group),
      blockedStatus: status === 'fake' ? 'not_blocked' : null,
      scanReportIds: reportId ? [reportId] : [],
    };

    try {
      await groupReviewsService.createReview(reviewData);

      updateStateAndPersist((prev) => ({
        ...prev,
        results: prev.results.map((item) => (
          groupKey(item) === groupKey(group)
            ? {
              ...item,
              manualDecision: status,
              reviewStatus: status,
              reviewedAt,
              reviewedBy: reviewerId,
              reviewReason: trimmedReason,
              blockedStatus: reviewData.blockedStatus,
            }
            : item
        )),
      }));
      setError('');
      setMessage(`Marked "${groupName}" as ${reviewLabel}.`);
    } catch (reviewError) {
      setError(`Error saving review: ${reviewError.message}`);
    }
  }, [updateStateAndPersist]);

  const summary = useMemo(() => summarize(autoState.results), [autoState.results]);
  const stateOptions = useMemo(() => {
    const current = String(autoState.targetState || '').trim();
    const options = current && !INDIAN_STATE_OPTIONS.includes(current)
      ? [current, ...INDIAN_STATE_OPTIONS]
      : INDIAN_STATE_OPTIONS;
    return [ANY_STATE_VALUE, ...options];
  }, [autoState.targetState]);

  const selectedStateValue = autoState.queryLocationMode === 'any'
    ? ANY_STATE_VALUE
    : autoState.targetState;

  const isPipelineActive = ['phase1', 'phase2', 'waiting'].includes(autoState?.phase || 'idle');

  return (
    <div className="autoscan-page">
      <header className="autoscan-hero">
        <div>
          <p className="autoscan-eyebrow">Global Shared Automation</p>
          <h1>Global AutoScan</h1>
        </div>
        <div className="autoscan-status-card">
          <span>Global Telegram Session</span>
          <strong>
            {tgSession.loading ? 'Checking...' : tgSession.authorized ? 'Authorized' : 'Not Connected'}
          </strong>
          <small>
            Pipeline Phase: {String(autoState?.phase || 'idle').toUpperCase()}
          </small>
          <button
            type="button"
            className="autoscan-text-btn"
            style={{ marginTop: 8, padding: 0, textAlign: 'left', fontWeight: 900 }}
            onClick={() => setShowSessionSetup(!showSessionSetup)}
          >
            {showSessionSetup ? 'Hide Session Setup' : 'Manage Session'}
          </button>
        </div>
      </header>

      {/* Global Telegram Session Login Panel */}
      {(showSessionSetup || !tgSession.authorized) && (
        <div className="autoscan-card" style={{ marginBottom: 24, background: '#f8fafc', borderColor: '#cbd5e1' }}>
          <div className="autoscan-card-head">
            <div>
              <p className="autoscan-eyebrow">Shared Session Setup</p>
              <h2>Dedicated Telegram Session</h2>
              <p style={{ color: '#64748b', margin: 0 }}>
                This session will be used globally for all users executing AutoScan.
              </p>
            </div>
            {tgSession.authorized && (
              <button type="button" className="autoscan-danger" onClick={handleLogoutTgSession} disabled={tgBusy}>
                Logout Global Session
              </button>
            )}
          </div>

          {!tgSession.authorized && (
            <div style={{ display: 'grid', gap: 14, maxWidth: 500, marginTop: 12 }}>
              <label className="autoscan-label">
                Telegram Phone Number (+CountryCode)
                <input
                  type="text"
                  placeholder="+917899001234"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  disabled={tgBusy || codeSent}
                  style={{ marginTop: 4, width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1' }}
                />
              </label>

              {!codeSent ? (
                <button type="button" className="autoscan-primary" onClick={handleSendTgCode} disabled={tgBusy}>
                  {tgBusy ? 'Sending OTP Code...' : 'Send Telegram OTP'}
                </button>
              ) : (
                <>
                  <label className="autoscan-label">
                    Telegram Verification Code
                    <input
                      type="text"
                      placeholder="12345"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      disabled={tgBusy}
                      style={{ marginTop: 4, width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1' }}
                    />
                  </label>

                  <label className="autoscan-label">
                    Two-Step Verification Password (If enabled)
                    <input
                      type="password"
                      placeholder="Password"
                      value={passwordInput}
                      onChange={(e) => setPasswordInput(e.target.value)}
                      disabled={tgBusy}
                      style={{ marginTop: 4, width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1' }}
                    />
                  </label>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" className="autoscan-primary" onClick={handleConfirmTgCode} disabled={tgBusy}>
                      {tgBusy ? 'Verifying...' : 'Authorize Telegram Session'}
                    </button>
                    <button type="button" className="autoscan-text-btn" onClick={() => setCodeSent(false)}>
                      Change Phone
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {(message || error) && (
        <div className={`autoscan-alert ${error ? 'is-error' : ''}`}>
          {error || message}
        </div>
      )}

      <section className="autoscan-grid">
        {/* Setup Card */}
        <div className="autoscan-card autoscan-config">
          <div className="autoscan-card-head">
            <div>
              <p className="autoscan-eyebrow">Setup Queue</p>
              <h2>Monitored Keywords ({autoState.watchlist.length})</h2>
            </div>
            {!isPipelineActive ? (
              <button
                type="button"
                className="autoscan-primary"
                onClick={handleRestartAllKeywords}
                disabled={!isStateLoaded || (!autoState.watchlist.length && !autoState.queue.length)}
              >
                Restart All Monitored Keywords Now
              </button>
            ) : (
              <button
                type="button"
                className="autoscan-danger"
                onClick={handleStopGlobalPipeline}
              >
                Stop Pipeline
              </button>
            )}
          </div>

          <label className="autoscan-label" htmlFor="autoscan-keywords">Add Keywords</label>
          <div className="autoscan-add-row">
            <textarea
              id="autoscan-keywords"
              value={keywordInput}
              onChange={(event) => setKeywordInput(event.target.value)}
              placeholder="Add keywords separated by comma or new line"
            />
            <button type="button" className="autoscan-secondary" onClick={addKeywords} disabled={!isStateLoaded}>
              Add
            </button>
          </div>

          <div className="autoscan-form-grid">
            <label>
              Schedule
              <select
                value={autoState.scheduleMode}
                onChange={(event) => updateStateAndPersist({ scheduleMode: event.target.value })}
              >
                <option value="weekly">Repeat weekly</option>
                <option value="monthly">Repeat monthly</option>
              </select>
            </label>

            <label>
              Location mode
              <select
                value={autoState.queryLocationMode}
                onChange={(event) => updateStateAndPersist({
                  queryLocationMode: event.target.value,
                  targetState: event.target.value === 'any' ? '' : autoState.targetState,
                })}
              >
                {locationModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </label>

            <label>
              State
              <select
                value={selectedStateValue}
                onChange={(event) => {
                  const value = event.target.value;
                  updateStateAndPersist({
                    targetState: value === ANY_STATE_VALUE ? '' : value,
                    queryLocationMode: value === ANY_STATE_VALUE
                      ? 'any'
                      : (autoState.queryLocationMode === 'any' ? 'state_only' : autoState.queryLocationMode),
                  });
                }}
              >
                {stateOptions.map((state) => (
                  <option key={state} value={state}>
                    {state === ANY_STATE_VALUE ? 'Any State' : state}
                  </option>
                ))}
              </select>
            </label>

            <label>
              City
              <input
                value={autoState.targetCity}
                onChange={(event) => updateStateAndPersist({ targetCity: event.target.value })}
                placeholder="Optional city"
              />
            </label>
          </div>
        </div>

        {/* Current Pipeline Status Card */}
        <div className="autoscan-card autoscan-current">
          <p className="autoscan-eyebrow">Pipeline Status</p>
          {isPipelineActive ? (
            <>
              <h2 style={{ textTransform: 'capitalize' }}>
                Phase: {autoState?.phase || 'idle'}
              </h2>
              {autoState?.phase === 'phase1' && (
                <div>
                  <p>Finding links for keywords...</p>
                  {autoState?.currentKeyword && (
                    <p style={{ color: '#0284c7', fontWeight: 600, margin: '6px 0' }}>
                      Active Keyword: <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{autoState.currentKeyword}</span>
                    </p>
                  )}
                  <p>Keywords Processed: <strong>{autoState?.phase1Progress?.done || 0} / {autoState?.phase1Progress?.total || 0}</strong></p>
                  <p>Discovered Links: <strong>{autoState?.discoveredLinksCount || 0}</strong></p>
                  <div className="autoscan-progress">
                    <span style={{ width: `${autoState?.phase1Progress?.total ? ((autoState?.phase1Progress?.done || 0) / autoState.phase1Progress.total) * 100 : 0}%` }} />
                  </div>
                </div>
              )}

              {autoState?.phase === 'phase2' && (
                <div>
                  <p>Analyzing Telegram Groups in Batches (100 links per batch)</p>
                  {autoState?.currentGroupLink && (
                    <p style={{ color: '#0284c7', fontWeight: 600, margin: '6px 0', wordBreak: 'break-all' }}>
                      Currently Inspecting: <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'monospace' }}>{autoState.currentGroupLink}</span>
                    </p>
                  )}
                  <p>Batch: <strong>{autoState?.phase2Progress?.batchDone || 0} / {autoState?.phase2Progress?.batchTotal || 0}</strong></p>
                  <p>Groups Analyzed: <strong>{autoState?.phase2Progress?.groupsDone || 0} / {autoState?.phase2Progress?.groupsTotal || 0}</strong></p>
                  <div className="autoscan-progress">
                    <span style={{ width: `${autoState?.phase2Progress?.groupsTotal ? ((autoState?.phase2Progress?.groupsDone || 0) / autoState.phase2Progress.groupsTotal) * 100 : 0}%` }} />
                  </div>
                </div>
              )}

              {autoState.phase === 'waiting' && (
                <div style={{ background: '#fff7ed', padding: 14, borderRadius: 12, border: '1px solid #ffedd5' }}>
                  <strong style={{ color: '#c2410c' }}>Rate-Limit Pause Active</strong>
                  <p style={{ margin: '6px 0 0', color: '#9a3412' }}>
                    Pausing for 10 minutes between batches to protect Telegram session.
                  </p>
                  {autoState.batchPauseUntil && (
                    <small style={{ color: '#c2410c' }}>
                      Resuming at: {new Date(autoState.batchPauseUntil).toLocaleTimeString()}
                    </small>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="autoscan-empty">
              <h2>Pipeline Idle</h2>
              <p>Add keywords to begin automatic scanning or click Restart All Monitored Keywords Now to re-scan.</p>
              {autoState.lastCycleFinishedAt && (
                <small>Last global cycle finished at: {new Date(autoState.lastCycleFinishedAt).toLocaleString()}</small>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Queue Lanes */}
      <section className="autoscan-lanes">
        <div className="autoscan-card">
          <div className="autoscan-card-head">
            <div>
              <p className="autoscan-eyebrow">Queue (Waiting Keywords)</p>
              <h2>{queueList.length} Pending Keywords</h2>
            </div>
            {queueList.length > 0 && (
              <button
                type="button"
                className="autoscan-text-btn"
                onClick={() => updateStateAndPersist((prev) => ({ ...prev, queue: [] }))}
                disabled={!isStateLoaded}
              >
                Clear Queue
              </button>
            )}
          </div>
          <div className="autoscan-chip-list">
            {queueList.length ? queueList.map((item, index) => {
              const queuedItem = normalizeQueueItem(item, autoState, index);
              if (!queuedItem) return null;

              return (
                <button key={queuedItem.id} type="button" className="autoscan-chip" onClick={() => removeQueuedKeyword(queuedItem.id)} disabled={!isStateLoaded}>
                  {queuedItem.searchPhrase}
                  <span>remove</span>
                </button>
              );
            }) : <p className="autoscan-muted">No keywords waiting in global queue.</p>}
          </div>
        </div>

        <div className="autoscan-card">
          <p className="autoscan-eyebrow">Completed Keywords</p>
          <h2>
            {watchlistList.filter((w) => !queueList.some((q) => queueIdentity(q) === queueIdentity(w))).length} Done Keywords
          </h2>
          <div className="autoscan-run-list">
            {watchlistList
              .filter((w) => !queueList.some((q) => queueIdentity(q) === queueIdentity(w)))
              .map((item) => (
                <div className="autoscan-run" key={item.id || item.keyword}>
                  <strong>{item.searchPhrase || item.keyword}</strong>
                  <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                    Completed / Watchlist
                  </span>
                </div>
              ))}
            {!watchlistList.filter((w) => !queueList.some((q) => queueIdentity(q) === queueIdentity(w))).length && (
              <p className="autoscan-muted">Completed keywords will appear here after scan.</p>
            )}
          </div>
        </div>
      </section>

      {/* Results View */}
      <section className="autoscan-card autoscan-results">
        <div className="autoscan-card-head">
          <div>
            <p className="autoscan-eyebrow">Global Monitoring Intelligence</p>
            <h2>{summary.total} Discovered & Analyzed Groups</h2>
          </div>
          <button type="button" className="autoscan-danger" onClick={clearResults} disabled={!isStateLoaded || !resultsList.length}>
            Clear Results
          </button>
        </div>

        <div className="autoscan-summary" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'all' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('all'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #cbd5e1', cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: filterVerdict === 'all' ? '#0f172a' : '#f8fafc',
              color: filterVerdict === 'all' ? '#ffffff' : '#334155'
            }}
          >
            All ({summary.total})
          </button>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'fraud' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('fraud'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #fca5a5', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              background: filterVerdict === 'fraud' ? '#dc2626' : '#fef2f2',
              color: filterVerdict === 'fraud' ? '#ffffff' : '#991b1b'
            }}
          >
            Fraud ({summary.fraud})
          </button>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'suspicious' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('suspicious'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #fde047', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              background: filterVerdict === 'suspicious' ? '#d97706' : '#fffbeb',
              color: filterVerdict === 'suspicious' ? '#ffffff' : '#92400e'
            }}
          >
            Suspicious ({summary.suspicious})
          </button>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'uncertain' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('uncertain'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #fed7aa', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              background: filterVerdict === 'uncertain' ? '#ea580c' : '#fff7ed',
              color: filterVerdict === 'uncertain' ? '#ffffff' : '#c2410c'
            }}
          >
            AI Uncertain ({summary.uncertain})
          </button>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'genuine' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('genuine'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #86efac', cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              background: filterVerdict === 'genuine' ? '#16a34a' : '#f0fdf4',
              color: filterVerdict === 'genuine' ? '#ffffff' : '#166534'
            }}
          >
            Genuine ({summary.genuine})
          </button>
          <button
            type="button"
            className={`autoscan-filter-pill ${filterVerdict === 'unmarked' ? 'active' : ''}`}
            onClick={() => { setFilterVerdict('unmarked'); setCurrentPage(1); }}
            style={{
              padding: '6px 14px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: filterVerdict === 'unmarked' ? '#475569' : '#f8fafc',
              color: filterVerdict === 'unmarked' ? '#ffffff' : '#475569'
            }}
          >
            Unmarked ({summary.unmarked})
          </button>
        </div>

        <div className="autoscan-result-list">
          {displayedResults.map((group, index) => {
            const actualIndex = (currentPage - 1) * PAGE_SIZE + index;
            return (
              <AutoScanGroupCard
                key={`${group.autoScanId || group.globalAutoScanOperationId || groupKey(group)}-${actualIndex}`}
                group={group}
                index={actualIndex}
                fallbackConfig={autoState}
                onMarkReview={handleMarkReview}
              />
            );
          })}

          {!filteredResults.length && (
            <div className="autoscan-empty autoscan-empty-results">
              <h3>{resultsList.length ? `No groups found matching '${filterVerdict}' filter` : 'No accumulated global results yet'}</h3>
              <p>{resultsList.length ? 'Try selecting a different filter above to view other groups.' : 'Discovered and analyzed groups from Phase 2 will be dynamically updated here.'}</p>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {filteredResults.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 12
          }}>
            <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredResults.length)} of {filteredResults.length} {filterVerdict !== 'all' ? `${filterVerdict} ` : ''}groups
            </span>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  className="autoscan-text-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', padding: '6px 12px' }}
                >
                  ← Previous
                </button>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="autoscan-text-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  style={{ opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', padding: '6px 12px' }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Confirmation Modal for Clearing Results */}
      {showClearConfirmModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#ffffff', borderRadius: 16, padding: 24, maxWidth: 460, width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              ⚠️ Clear All Global Results?
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
              Are you sure you want to clear all <strong>{autoState.results.length}</strong> accumulated global AutoScan results? This action will remove the current results from your dashboard.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                type="button"
                className="autoscan-text-btn"
                style={{ padding: '8px 16px' }}
                onClick={() => setShowClearConfirmModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="autoscan-danger"
                style={{ padding: '8px 16px', borderRadius: 8 }}
                onClick={handleConfirmClearResults}
              >
                Yes, Clear Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
